
import React from "react";
import csmGrayTextLogin from "../../assets/images/csmGrayTextLogin.png";

const TermsOfUse = () => {
  return (
    <div className="max-w-4xl mx-auto px-6 py-12 text-gray-800">
      <div className="flex justify-center mb-32">
        <img
          src={csmGrayTextLogin}
          alt="ProTeams Logo"
          className="h-16 w-auto object-contain"
        />
      </div>

      <h1 className="text-3xl font-bold mb-32 text-center">
        Terms of Use for ProTeams
      </h1>

      <p className="mb-16">
        Welcome to <strong>ProTeams</strong>, a mobile app developed by{" "}
        <strong>Verdant Facilities Maintenance, LLC</strong>, a New York State company.
        These Terms of Use (the "Terms") govern your use of the ProTeams app (the "App").
        By using the App, you agree to be bound by these Terms.
      </p>

      <Section title="1. Introduction">
        Welcome to ProTeams. By using the App, you agree to comply with and be bound by these Terms.
      </Section>

      <Section title="2. Definitions">
        “Verdant” means Verdant Facilities Maintenance, LLC, a New York State company. <br />
        “App” means the ProTeams mobile app. <br />
        “User” means any individual who uses the App.
      </Section>

      <Section title="3. License">
        Verdant grants you a non-exclusive, non-transferable, limited license to use the App on a
        single mobile device. You may not rent, lease, lend, sell, redistribute, or sublicense the
        App.
      </Section>

      <Section title="4. Restrictions">
        You agree not to:
        <ul className="list-disc ml-24 mt-8">
          <li>Use the App for any illegal or unauthorized purpose.</li>
          <li>Modify, adapt, or hack the App.</li>
          <li>Reverse engineer the App.</li>
          <li>Remove any copyright or proprietary notices.</li>
          <li>Use the App in a way that disrupts servers or networks.</li>
        </ul>
      </Section>

      <Section title="5. Content">
        The App may allow you to post or upload content (the “Content”). You are solely responsible
        for the Content you submit and agree not to submit Content that is illegal, offensive,
        infringing, misleading, or otherwise objectionable. Verdant reserves the right to remove any
        Content at any time.
      </Section>

      <Section title="6. Indemnification">
        You agree to indemnify and hold Verdant harmless from any and all claims, damages, and
        expenses arising out of your use of the App.
      </Section>

      <Section title="7. Disclaimer of Warranty">
        THE APP IS PROVIDED “AS IS” AND VERDANT MAKES NO WARRANTIES, EXPRESS OR IMPLIED, INCLUDING
        BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY OR FITNESS FOR A PARTICULAR PURPOSE.
      </Section>

      <Section title="8. Limitation of Liability">
        IN NO EVENT SHALL VERDANT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, OR CONSEQUENTIAL DAMAGES,
        INCLUDING LOSS OF PROFITS OR DATA, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
      </Section>

      <Section title="9. Governing Law">
        These Terms shall be governed by the laws of the State of New York, without regard to
        conflict of laws principles.
      </Section>

      <Section title="10. Entire Agreement">
        These Terms constitute the entire agreement between you and Verdant and supersede all prior
        agreements.
      </Section>

      <Section title="11. Severability">
        If any provision of these Terms is held invalid, the remaining provisions will remain in
        effect.
      </Section>

      <Section title="12. Changes to Terms">
        Verdant may update these Terms from time to time. Continued use of the App after changes
        constitutes acceptance of the new Terms.
      </Section>

      <Section title="13. Contact Information">
        If you have any questions about these Terms, please contact Verdant at:
        <div className="mt-8">
          <p>Verdant Facilities Maintenance, LLC</p>
          <p>123 Main Street</p>
          <p>Anytown, NY 12345</p>
        </div>
      </Section>

      <Section title="14. Effective Date">These Terms are effective as of July 10, 2023.</Section>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div className="mb-24">
    <h2 className="text-lg font-semibold mb-8">{title}</h2>
    <p className="leading-relaxed">{children}</p>
  </div>
);

export default TermsOfUse;


const PrivacySection = ({ title, children }) => (
  <div className="mb-24">
    <h2 className="text-lg font-semibold mb-8">{title}</h2>
    <div className="leading-relaxed">{children}</div>
  </div>
);


export const PrivacyPolicy = () => {
  return (
    <div className="max-w-4xl mx-auto px-6 py-12 text-gray-800">
      <div className="flex justify-center mb-32">
        <img
          src={csmGrayTextLogin}
          alt="ProTeams Logo"
          className="h-16 w-auto object-contain"
        />
      </div>

      <h1 className="text-3xl font-bold mb-32 text-center">Privacy Policy</h1>

      <PrivacySection title="Introduction">
        Thank you for choosing <strong>ProTeams</strong>, a mobile and online platform
        developed by <strong>Verdant Facilities Maintenance, LLC</strong>. Your privacy is
        very important to us. This Privacy Policy explains how we collect, use, and protect
        your personal information when you use our services.
      </PrivacySection>

      <PrivacySection title="Personal Information We Collect">
        We collect personal information that uniquely identifies, relates to, or can be
        reasonably linked with you. This may include:
        <ul className="list-disc ml-24 mt-8">
          <li>Identifiers such as your name, email address, and phone number.</li>
          <li>Contact details such as your mailing address.</li>
          <li>Demographic information like age, gender, and location.</li>
          <li>Financial information such as payment details.</li>
          <li>
            Transactional data including services booked and payments made.
          </li>
          <li>
            Usage information such as pages you visit and features you interact with.
          </li>
          <li>Device and network details including IP address and device type.</li>
          <li>
            Cookie information stored on your browser to improve your user experience.
          </li>
        </ul>
      </PrivacySection>

      <PrivacySection title="How We Use Your Personal Information">
        We use your personal information to:
        <ul className="list-disc ml-24 mt-8">
          <li>Provide and manage the services you request.</li>
          <li>Communicate with you about your account or bookings.</li>
          <li>
            Send you updates, promotional content, and service-related information.
          </li>
          <li>Improve our platform and develop new features.</li>
          <li>Comply with legal obligations and protect our rights.</li>
        </ul>
      </PrivacySection>

      <PrivacySection title="Sharing Your Personal Information">
        We may share your personal information with trusted third parties, including:
        <ul className="list-disc ml-24 mt-8">
          <li>
            Service providers assisting with operations such as payments or support.
          </li>
          <li>
            Marketing partners who deliver communications on our behalf.
          </li>
          <li>
            Business partners involved in providing value-added services.
          </li>
          <li>Government agencies, when required by law.</li>
        </ul>
      </PrivacySection>

      <PrivacySection title="Your Rights">
        You have the following rights regarding your personal information:
        <ul className="list-disc ml-24 mt-8">
          <li>Access your personal data.</li>
          <li>Request corrections to inaccurate information.</li>
          <li>Request deletion of your personal information.</li>
          <li>Object to or restrict certain data processing activities.</li>
          <li>Request a copy of your data in a portable format.</li>
        </ul>
        To exercise your rights, please contact us at{" "}
        <a
          href="mailto:support@proteams.com"
          className="text-blue-600 underline"
        >
          support@proteams.com
        </a>
        .
      </PrivacySection>

      <PrivacySection title="Security">
        We take appropriate technical and organizational measures to safeguard your
        personal information, including:
        <ul className="list-disc ml-24 mt-8">
          <li>Using encryption and secure servers.</li>
          <li>Restricting access to authorized personnel only.</li>
          <li>
            Training our employees on best practices for data protection and privacy.
          </li>
        </ul>
      </PrivacySection>

      <PrivacySection title="Changes to This Privacy Policy">
        We may update this Privacy Policy from time to time to reflect changes in our
        practices or for other operational, legal, or regulatory reasons. Any significant
        changes will be communicated via email or through a notice in the App.
      </PrivacySection>

      <PrivacySection title="Contact Us">
        If you have any questions about this Privacy Policy, please contact us:
        <div className="mt-2">
          <p>Email: <a href="mailto:support@proteams.com" className="text-blue-600 underline">support@proteams.com</a></p>
          <p>Phone: (555) 123-4567</p>
        </div>
      </PrivacySection>

      <p className="mt-32 text-sm text-gray-600 text-center">
        Thank you for trusting <strong>ProTeams</strong> with your information.
      </p>
    </div>
  );
};
